import { useState, useEffect, useCallback } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { analysisApi } from '@/lib/api/analysis'
import { monitorApi } from '@/lib/api/monitor'
import { useAllProjects } from '@/lib/hooks/useProjects'
import { RefreshCw, TrendingUp, TrendingDown, Minus, BarChart3, Loader2, Hash, Globe, Users, ExternalLink, X } from 'lucide-react'

const SOURCE_PLATFORM_NAMES: Record<string, string> = {
  weibo: '微博', wechat: '微信', zhihu: '知乎', douyin: '抖音',
  bilibili: 'B站', xiaohongshu: '小红书', kuaishou: '快手',
  video: '视频号', news: '新闻网站', app: '客户端', forum: '论坛', tieba: '贴吧',
}

const NEED_LOGIN_PLATFORMS = ['weibo', 'xiaohongshu', 'zhihu', 'douyin', 'bilibili', 'kuaishou']

function useProjectIdParam() {
  const location = useLocation()
  const [projectId, setProjectId] = useState('')
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    setProjectId(sp.get('projectId') || '')
  }, [location.pathname, location.search])
  return projectId
}

export function AnalysisPage() {
  const { t } = useTranslation()
  const { projects, loading: projLoading } = useAllProjects()
  const routeProjectId = useProjectIdParam()
  const [projectId, setProjectId] = useState(routeProjectId)
  const [projectName, setProjectName] = useState('')
  const [timePeriod, setTimePeriod] = useState(7)
  const [loading, setLoading] = useState(false)
  const [overview, setOverview] = useState<any>(null)
  const [emotionData, setEmotionData] = useState<any>(null)
  const [hotEvents, setHotEvents] = useState<any[]>([])
  const [highWords, setHighWords] = useState<any[]>([])
  const [dataSource, setDataSource] = useState<any[]>([])
  const [trendData, setTrendData] = useState<any[]>([])

  // Event detail dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false)
  const [eventDialogName, setEventDialogName] = useState('')
  const [eventArticles, setEventArticles] = useState<any[]>([])
  const [eventArticlesLoading, setEventArticlesLoading] = useState(false)
  const [eventArticleDetail, setEventArticleDetail] = useState<any>(null)

  const timeOptions = [
    { value: 1, label: t('monitor.hours24') },
    { value: 2, label: t('monitor.today') },
    { value: 4, label: t('monitor.days3') },
    { value: 5, label: t('monitor.days7') },
    { value: 6, label: t('monitor.days15') },
    { value: 7, label: t('monitor.days30') },
  ]

  // Auto-select project from route search params or first available
  useEffect(() => {
    if (routeProjectId && routeProjectId !== projectId) {
      setProjectId(routeProjectId)
      const proj = projects.find(p => p.project_id === routeProjectId)
      setProjectName(proj?.project_name || '')
    } else if (!projectId && !routeProjectId && projects.length > 0) {
      setProjectId(projects[0].project_id)
      setProjectName(projects[0].project_name)
    }
    if (projectId) {
      const proj = projects.find(p => p.project_id === projectId)
      if (proj) setProjectName(proj.project_name)
    }
  }, [projects, projectId, routeProjectId])

  const fetchData = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [ov, em, events, words, source, trend] = await Promise.allSettled([
        analysisApi.dataOverview(projectId, timePeriod),
        analysisApi.emotionalProportion(projectId, timePeriod),
        analysisApi.hotEventRanking(projectId, timePeriod),
        analysisApi.highwordCloud(projectId, timePeriod),
        analysisApi.dataSourceDistribution(projectId, timePeriod),
        analysisApi.keywordEmotionTrend(projectId, timePeriod),
      ])

      // Unwrap mock response: { code, msg, data } → extract .data
      const unwrap = (r: any) => r?.value ? ((r.value as any)?.data ?? r.value) : null

      if (ov.status === 'fulfilled') setOverview(unwrap(ov))
      if (em.status === 'fulfilled') setEmotionData(unwrap(em))
      if (events.status === 'fulfilled') {
        const evData = unwrap(events) || []
        setHotEvents(Array.isArray(evData) ? evData : [])
      }
      if (words.status === 'fulfilled') {
        const wData = unwrap(words) || []
        setHighWords(Array.isArray(wData) ? wData : [])
      }
      if (source.status === 'fulfilled') {
        const sData = unwrap(source) || []
        setDataSource(Array.isArray(sData) ? sData : [])
      }
      if (trend.status === 'fulfilled') {
        const tData = unwrap(trend) || []
        setTrendData(Array.isArray(tData) ? tData : [])
      }
    } catch (err) {
      console.error('Analysis fetch failed:', err)
    } finally {
      setLoading(false)
    }
  }, [projectId, timePeriod])

  // Click hot event → load articles for that platform
  const handleEventClick = useCallback(async (event: any) => {
    const platform = event.source_name || event.platform || ''
    const eventName = event.event_name || event.name || event.title || ''
    setEventDialogOpen(true)
    setEventDialogName(eventName)
    setEventArticles([])
    setEventArticlesLoading(true)
    setEventArticleDetail(null)
    try {
      const res = await monitorApi.getDataList({
        projectid: projectId,
        pagenum: 1,
        classify: platform,
        time_period: timePeriod,
      })
      const data = res?.data ?? res
      const list = Array.isArray(data) ? data : (data?.list || data?.data || [])
      setEventArticles(list)
    } catch (err) {
      console.error('Failed to load event articles:', err)
    } finally {
      setEventArticlesLoading(false)
    }
  }, [projectId, timePeriod])

  const isSearchUrl = (url: string) => {
    if (!url) return false
    return /baidu\.com\/s|s\.weibo\.com|weixin\.sogou\.com|zhihu\.com\/search|xiaohongshu\.com\/search|search\.bilibili\.com|douyin\.com\/search|kuaishou\.com\/search|channels\.weixin\.qq\.com\/search|tieba\.baidu\.com\/f\?/.test(url)
  }

  const formatDate = (dt: string) => {
    if (!dt) return '--'
    const d = new String(dt).replace('T', ' ').substring(0, 16)
    return d
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleProjectChange = (id: string) => {
    setProjectId(id)
    const proj = projects.find(p => p.project_id === id)
    setProjectName(proj?.project_name || '')
  }

  const emotionPct = (type: string) => {
    if (!emotionData) return 0
    return emotionData[type] || emotionData[`${type}_percent`] || 0
  }

  // Trend chart helper
  const maxTrend = Math.max(
    ...trendData.flatMap(d => [d.positive || 0, d.neutral || 0, d.negative || 0]),
  ) || 1

  return (
    <div className="space-y-4">
      {/* Header with project selector and time */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">{t('analysis.title')}</h2>
        <div className="flex items-center gap-3">
          <Select value={projectId} onValueChange={handleProjectChange}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={projLoading ? t('common.loading') : t('monitor.projectName')} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={timePeriod}
            onChange={(e) => setTimePeriod(Number(e.target.value))}
          >
            {timeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('analysis.update')}
          </Button>
        </div>
      </div>

      {projectName && (
        <p className="text-sm text-muted-foreground">
          {t('monitor.projectName')}: <span className="font-medium text-foreground">{projectName}</span>
        </p>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex w-full flex-wrap h-auto">
          <TabsTrigger value="overview">{t('analysis.dataOverview')}</TabsTrigger>
          <TabsTrigger value="emotion">{t('analysis.emotionalProportion')}</TabsTrigger>
          <TabsTrigger value="trend">{t('analysis.keywordEmotionTrend')}</TabsTrigger>
          <TabsTrigger value="events">{t('analysis.hotEventRanking')}</TabsTrigger>
          <TabsTrigger value="words">{t('analysis.highwordCloud')}</TabsTrigger>
          <TabsTrigger value="source">{t('analysis.dataSourceDistribution')}</TabsTrigger>
        </TabsList>

        {/* Data Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('analysis.totalArticles')}</p>
                    <p className="text-3xl font-bold mt-2">{overview?.total || overview?.total_count || '--'}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                    <BarChart3 className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('analysis.newsCount')}</p>
                    <p className="text-3xl font-bold mt-2">{overview?.news || overview?.news_count || '--'}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50">
                    <Hash className="h-6 w-6 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('analysis.socialCount')}</p>
                    <p className="text-3xl font-bold mt-2">{overview?.social || overview?.social_count || '--'}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-50">
                    <Users className="h-6 w-6 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Emotion Proportion Tab */}
        <TabsContent value="emotion">
          <Card>
            <CardHeader>
              <CardTitle>{t('analysis.emotionalProportion')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-around py-8">
                <EmotionCircle
                  icon={TrendingUp}
                  label={t('monitor.positive')}
                  value={emotionPct('positive')}
                  color="green"
                />
                <EmotionCircle
                  icon={Minus}
                  label={t('monitor.neutral')}
                  value={emotionPct('neutral')}
                  color="gray"
                />
                <EmotionCircle
                  icon={TrendingDown}
                  label={t('monitor.negative')}
                  value={emotionPct('negative')}
                  color="red"
                />
              </div>
              {/* Bar chart representation */}
              <div className="mt-6 space-y-3">
                <EmotionBar label={t('monitor.positive')} value={emotionPct('positive')} color="bg-green-500" />
                <EmotionBar label={t('monitor.neutral')} value={emotionPct('neutral')} color="bg-gray-400" />
                <EmotionBar label={t('monitor.negative')} value={emotionPct('negative')} color="bg-red-500" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trend Tab */}
        <TabsContent value="trend">
          <Card>
            <CardHeader>
              <CardTitle>{t('analysis.keywordEmotionTrend')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" /> : trendData.length > 0 ? (
                <div className="space-y-3">
                  {/* Legend */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-green-500" />{t('monitor.positive')}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-gray-400" />{t('monitor.neutral')}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" />{t('monitor.negative')}</span>
                  </div>
                  {/* Stacked bars */}
                  {trendData.map((d, i) => (
                    <div key={i} className="space-y-1">
                      <span className="text-xs text-muted-foreground">{d.date}</span>
                      <div className="flex h-6 gap-0.5 overflow-hidden rounded">
                        <div className="h-full bg-green-500 flex items-center justify-center" style={{ width: `${(d.positive / maxTrend) * 100}%` }} title={`正面: ${d.positive}`}>
                          {d.positive > maxTrend * 0.15 && <span className="text-[10px] text-white">{d.positive}</span>}
                        </div>
                        <div className="h-full bg-gray-400 flex items-center justify-center" style={{ width: `${(d.neutral / maxTrend) * 100}%` }} title={`中性: ${d.neutral}`}>
                          {d.neutral > maxTrend * 0.15 && <span className="text-[10px] text-white">{d.neutral}</span>}
                        </div>
                        <div className="h-full bg-red-500 flex items-center justify-center" style={{ width: `${(d.negative / maxTrend) * 100}%` }} title={`负面: ${d.negative}`}>
                          {d.negative > maxTrend * 0.15 && <span className="text-[10px] text-white">{d.negative}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-3">
                  <BarChart3 className="h-16 w-16 opacity-30" />
                  <p>{t('common.noData')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hot Events Tab */}
        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle>{t('analysis.hotEventRanking')}</CardTitle>
            </CardHeader>
            <CardContent>
              {hotEvents.length > 0 ? (
                <div className="space-y-2">
                  {hotEvents.slice(0, 10).map((event: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 cursor-pointer transition-colors"
                      onClick={() => handleEventClick(event)}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                          i < 3 ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'
                        }`}>
                          {i + 1}
                        </span>
                        <span className="text-sm truncate">{event.event_name || event.name || event.title}</span>
                      </div>
                      <Badge variant="secondary">{event.count || event.hot || '--'}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-2">
                  <p>{t('common.noData')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Word Cloud Tab */}
        <TabsContent value="words">
          <Card>
            <CardHeader>
              <CardTitle>{t('analysis.highwordCloud')}</CardTitle>
            </CardHeader>
            <CardContent>
              {highWords.length > 0 ? (
                <div className="flex flex-wrap gap-2 justify-center py-8">
                  {highWords.map((word: any, i: number) => {
                    const size = Math.max(12, Math.min(32, (word.count || word.weight || 10) / 3))
                    return (
                      <span
                        key={i}
                        className="cursor-pointer rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/20 transition-colors"
                        style={{ fontSize: `${size}px` }}
                      >
                        {word.word || word.name || word.keyword}
                      </span>
                    )
                  })}
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-2">
                  <Hash className="h-12 w-12 opacity-30" />
                  <p>{t('common.noData')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Source Tab */}
        <TabsContent value="source">
          <Card>
            <CardHeader>
              <CardTitle>{t('analysis.dataSourceDistribution')}</CardTitle>
            </CardHeader>
            <CardContent>
              {dataSource.length > 0 ? (
                <div className="space-y-3">
                  {dataSource.map((source: any, i: number) => {
                    const max = Math.max(...dataSource.map((s: any) => s.count || s.value || 0))
                    const pct = max > 0 ? ((source.count || source.value || 0) / max) * 100 : 0
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <Globe className="h-3 w-3 text-muted-foreground" />
                            {source.name || source.source_name || source.type}
                          </span>
                          <span className="font-medium">{source.count || source.value}</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted">
                          <div className="h-2 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-2">
                  <Globe className="h-12 w-12 opacity-30" />
                  <p>{t('common.noData')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Event Detail Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{eventDialogName}</DialogTitle>
          </DialogHeader>
          {eventArticleDetail ? (
            <div className="overflow-y-auto flex-1 space-y-3 p-1">
              <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" onClick={() => setEventArticleDetail(null)}>← 返回列表</Button>
              </div>
              <h3 className="text-lg font-semibold">{eventArticleDetail.title}</h3>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>来源: {eventArticleDetail.sourceName || eventArticleDetail.source_name || '--'}</span>
                <span>平台: {SOURCE_PLATFORM_NAMES[eventArticleDetail.sourcePlatform || eventArticleDetail.source_platform] || '--'}</span>
                <span>时间: {formatDate(eventArticleDetail.publishTime || eventArticleDetail.publish_time)}</span>
                <span>情感: <span className={eventArticleDetail.emotionalIndex >= 1 ? 'text-green-500' : eventArticleDetail.emotionalIndex <= -1 ? 'text-red-500' : 'text-gray-400'}>{eventArticleDetail.emotionalIndex >= 1 ? '正面' : eventArticleDetail.emotionalIndex <= -1 ? '负面' : '中性'}</span></span>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{eventArticleDetail.content || '--'}</p>
              {(() => {
                const url = eventArticleDetail.url || ''
                if (!url) return null
                const isSearch = isSearchUrl(url)
                const platform = eventArticleDetail.sourcePlatform || eventArticleDetail.source_platform || ''
                const needLogin = NEED_LOGIN_PLATFORMS.includes(platform)
                return (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-500 hover:underline">
                    {isSearch ? `在${SOURCE_PLATFORM_NAMES[platform] || '来源'}中搜索` : '原文链接'}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )
              })()}
              {(() => {
                const platform = eventArticleDetail.sourcePlatform || eventArticleDetail.source_platform || ''
                return NEED_LOGIN_PLATFORMS.includes(platform) ? (
                  <p className="text-xs text-yellow-600">⚠ 可能需要登录后才能查看完整内容</p>
                ) : null
              })()}
            </div>
          ) : eventArticlesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : eventArticles.length > 0 ? (
            <div className="overflow-y-auto flex-1 space-y-2 p-1">
              {eventArticles.map((article: any, i: number) => (
                <div
                  key={i}
                  className="rounded-lg border p-3 hover:bg-accent/50 cursor-pointer transition-colors"
                  onClick={() => setEventArticleDetail(article)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium truncate flex-1">{article.title}</span>
                    <span className={`text-xs shrink-0 ${article.emotionalIndex >= 1 ? 'text-green-500' : article.emotionalIndex <= -1 ? 'text-red-500' : 'text-gray-400'}`}>
                      {article.emotionalIndex >= 1 ? '正面' : article.emotionalIndex <= -1 ? '负面' : '中性'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>{SOURCE_PLATFORM_NAMES[article.sourcePlatform || article.source_platform] || article.sourcePlatform || article.source_platform}</span>
                    <span>{formatDate(article.publishTime || article.publish_time)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <p>{t('common.noData')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function EmotionCircle({ icon: Icon, label, value, color }: { icon: any, label: string, value: number, color: string }) {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-500',
    gray: 'bg-gray-100 text-gray-400',
    red: 'bg-red-100 text-red-500',
  }
  return (
    <div className="text-center">
      <div className={`mx-auto flex h-24 w-24 items-center justify-center rounded-full ${colorClasses[color]}`}>
        <Icon className="h-10 w-10" />
      </div>
      <p className="mt-3 font-medium">{label}</p>
      <p className={`text-2xl font-bold ${color === 'green' ? 'text-green-500' : color === 'red' ? 'text-red-500' : 'text-gray-400'}`}>
        {value}%
      </p>
    </div>
  )
}

function EmotionBar({ label, value, color }: { label: string, value: number, color: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="font-medium">{value}%</span>
      </div>
      <div className="h-3 rounded-full bg-muted">
        <div className={`h-3 rounded-full ${color}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  )
}
